import React from "react";
import cx from "classnames";
import { FilterTag, IFilterTagProps } from "../../../Atoms/FilterTag/FilterTag";
import { debounce } from "../../../../generics/debounce";
import { isSmallDeviceWidth } from "../../../../generics/dom-extensions";
import { IFilterControlTranslations } from "../../../../translations/translationModels";

import "./TagsPanel.scss";

export interface ITagsPanelProps {
  /**
   * texts
   */
  texts: IFilterControlTranslations;
  /**
   *  Collection of selected tags  to be displayed in the filter control
   */
  tags: IFilterTagProps[];
  /**
   * Callback for Clear All click
   */
  onClearAllFilters?: (tags: IFilterTagProps[]) => void;
  /**
   * Callback for mobile counter click
   */
  onTagCounterClick?: (
    event: React.MouseEvent<HTMLDivElement>,
    tags: IFilterTagProps[]
  ) => void;
  /**
   * Optional: extra content rendered at the end of the tags row (e.g. pagination controls).
   * When provided, the row stays visible/expanded even if there are no tags, so the content
   * isn't clipped by the collapsed (height: 0) empty state.
   */
  endContent?: React.ReactNode;
}

interface ITagsPanelState {
  lastShownTagIndex: number;
  isTagsHidden: boolean;
  tagsLength: number;
  hasVisibleEndContent: boolean;
}

export class TagsPanel extends React.Component<
  ITagsPanelProps,
  ITagsPanelState
> {
  private readonly tagsAreaRef = React.createRef<HTMLDivElement>();
  private readonly hiddenCalcAreaRef = React.createRef<HTMLDivElement>();
  private readonly endContentRef = React.createRef<HTMLDivElement>();
  private endContentObserver?: MutationObserver;

  constructor(props: ITagsPanelProps) {
    super(props);

    this.state = {
      lastShownTagIndex: props.tags.length,
      isTagsHidden: false,
      tagsLength: props.tags.length,
      hasVisibleEndContent: false,
    };

    this.onResize = debounce(this.onResize.bind(this), 100);
  }

  public componentDidMount() {
    this.observeEndContent();
    this.syncEndContentVisibility();
    this.reflow(this.props.tags);
    window.addEventListener("resize", this.onResize);
  }

  public componentDidUpdate(
    prevProps: Readonly<ITagsPanelProps>,
    prevState: Readonly<ITagsPanelState>
  ) {
    if (
      prevProps.tags !== this.props.tags ||
      this.props.tags.length !== prevState.tagsLength ||
      prevProps.endContent !== this.props.endContent
    ) {
      this.observeEndContent();
      this.syncEndContentVisibility();
      this.reflow(this.props.tags);
    }
  }

  public componentWillUnmount() {
    window.removeEventListener("resize", this.onResize);
    this.endContentObserver?.disconnect();
  }

  public render() {
    const { tags, texts, endContent } = this.props;
    const isWithTags = tags.length > 0;
    const hasEndContent = this.state.hasVisibleEndContent;
    const isPanelVisible = isWithTags || hasEndContent;

    return (
      <div
        className={cx(
          "c-tags-panel-wrapper g-col-lg-12",
          hasEndContent && "c-tags-panel-wrapper--with-end-content"
        )}
      >
        <div
          ref={this.tagsAreaRef}
          className={cx(
            "c-tags-panel",
            isPanelVisible && "c-tags-panel--with-tags flex items-center gap-1",
            hasEndContent && "c-tags-panel--with-end-content"
          )}
        >
          {tags.slice(0, this.state.lastShownTagIndex).map((item) => {
            return this.renderFilterTag(item);
          })}
          {this.state.isTagsHidden && (
            <FilterTag
              text={`+${tags.length - this.state.lastShownTagIndex}`}
              className="c-tags-panel__more-tags"
              onClick={this.handleTagCounterClick}
            />
          )}
          {isWithTags && (
            <span className="mx-1 flex-shrink-0 md:mx-0">
              <FilterTag
                text={`${tags.length}${texts.filtersMobile}`}
                className="c-tags-panel__tag-counter"
                onClick={this.handleTagCounterClick}
              />
              <a
                className="c-tags-panel__clear-all g-alternative-link g-alternative-link--lg inline-block"
                onClick={this.handleClearAllFilters}
                tabIndex={0}
              >
                {texts.clearAll}
              </a>
            </span>
          )}
          <div
            ref={this.hiddenCalcAreaRef}
            className="c-tags-panel__hidden-calc-area"
            aria-hidden={true}
          />
          {endContent && (
            <div ref={this.endContentRef} className="c-tags-panel__end-content">
              {endContent}
            </div>
          )}
        </div>
      </div>
    );
  }

  readonly renderFilterTag = (tag: IFilterTagProps) => {
    return (
      <FilterTag
        {...tag}
        className="c-tags-panel__active-tag"
        key={`${tag.id}-${tag.text}`}
      />
    );
  };

  private handleTagCounterClick = (event: React.MouseEvent<HTMLDivElement>) => {
    this.props.onTagCounterClick &&
      this.props.onTagCounterClick(event, this.props.tags);
  };

  private handleClearAllFilters = () => {
    this.props.onClearAllFilters &&
      this.props.onClearAllFilters(this.props.tags);
  };

  private onResize() {
    this.syncEndContentVisibility();
    this.reflow(this.props.tags);
  }

  private syncEndContentVisibility() {
    const endContentElement = this.endContentRef.current;
    const hasPaginationControl = Boolean(
      endContentElement?.querySelector(".c-pagination-control")
    );
    const hasNonEmptyElement = Boolean(
      endContentElement?.firstElementChild &&
      endContentElement.firstElementChild.textContent?.trim()
    );
    const hasVisibleEndContent = Boolean(
      hasPaginationControl || hasNonEmptyElement
    );

    if (hasVisibleEndContent !== this.state.hasVisibleEndContent) {
      this.setState({ hasVisibleEndContent });
    }
  }

  private observeEndContent() {
    if (
      typeof MutationObserver === "undefined" ||
      !this.endContentRef.current
    ) {
      return;
    }

    this.endContentObserver?.disconnect();
    this.endContentObserver = new MutationObserver(() => {
      this.syncEndContentVisibility();
    });
    this.endContentObserver.observe(this.endContentRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  // @ts-ignore
  private reflow(tags) {
    if (!tags.length) {
      if (
        this.state.isTagsHidden ||
        this.state.lastShownTagIndex !== 0 ||
        this.state.tagsLength !== 0
      ) {
        this.setState({
          isTagsHidden: false,
          lastShownTagIndex: 0,
          tagsLength: 0,
        });
      }

      return;
    }

    if (isSmallDeviceWidth()) {
      return;
    }

    const nextState = this.getNewState(tags);

    if (
      nextState.isTagsHidden !== this.state.isTagsHidden ||
      nextState.lastShownTagIndex !== this.state.lastShownTagIndex ||
      nextState.tagsLength !== this.state.tagsLength
    ) {
      this.setState(nextState);
    }
  }

  // @ts-ignore
  private getNewState = (tags) => {
    this.fillCalcArea(tags);
    const index = this.putEllipsis(this.calcIndex(), tags);
    const isTagsHidden = index < tags.length;
    const lastShownTagIndex = isTagsHidden ? index : tags.length;

    return { isTagsHidden, lastShownTagIndex, tagsLength: tags.length };
  };

  public calcIndex = () => {
    let element = this.hiddenCalcAreaRef.current
      ?.firstElementChild as HTMLElement;
    let index = 0;

    while (element) {
      if (element.offsetTop > 0) {
        break;
      }

      index++;
      element = element.nextElementSibling as HTMLElement;
    }

    return index;
  };

  // @ts-ignore
  private putEllipsis(index, tags) {
    if (index === tags.length) {
      return index;
    }

    const units = tags.slice(0, index);
    this.fillCalcArea(
      units,
      `<span class='c-tags-panel__hidden-calc-area__unit'>+99</span>`
    );
    const nodeEllipsis = this.hiddenCalcAreaRef.current
      ?.lastElementChild as HTMLElement;

    return nodeEllipsis && nodeEllipsis.offsetTop > 0 ? index - 1 : index;
  }

  private fillCalcArea(tags: IFilterTagProps[], end = "") {
    if (this.hiddenCalcAreaRef.current) {
      this.hiddenCalcAreaRef.current.innerHTML =
        tags
          .map(
            (c) =>
              `<span class='c-tags-panel__hidden-calc-area__unit ${c.className}'>${c.text}</span>`
          )
          .join("") + end;
    }
  }
}
